<!-- Audit Management -->
<template>
  <div class="am-list-page">
    <!-- 헤더 -->
    <div class="am-header">
      <div class="header-controls">
        <div class="year-month-selector">
          <select v-model="selectedYear" class="year-select">
            <option v-for="year in yearOptions" :key="year" :value="year">
              {{ year }}년
            </option>
          </select>
          <button @click="handleSearch" class="search-btn">검색</button>
          <span class="title"
            >&lt; {{ selectedYear }}년 감사 관리 내역 &gt;</span
          >
        </div>
        <div class="search-controls">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="매장검색"
            class="search-input"
            @input="onQuickFilterChanged"
          />
        </div>
      </div>
    </div>

    <!-- 그리드 -->
    <div class="am-grid-container">
      <ag-grid-vue
        class="ag-am-balham"
        :theme="theme"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :gridOptions="gridOptions"
        :rowHeight="rowHeight"
        :pinnedTopRowData="pinnedTopRowData"
        @grid-ready="onGridReady"
        @cell-double-clicked="onCellDoubleClicked"
      ></ag-grid-vue>
    </div>
    <!-- 감사 상세 팝업 -->
    <div v-if="showAuditModal" class="audit-modal-overlay">
      <div class="audit-modal">
        <div class="audit-modal-header">
          <div>
            <strong>감사관리</strong>
          </div>
          <div>
            <button class="btn-close" @click="closeAuditModal">X</button>
          </div>
        </div>

        <div class="audit-modal-body">
          <div class="audit-info">
            <div><strong>점번:</strong> {{ modalVanBizCd }}</div>
            <div><strong>본부명:</strong> {{ modalbranchNm }}</div>
            <div><strong>점명:</strong> {{ modalShopNm }}</div>
          </div>

          <table class="audit-table">
            <thead>
              <tr style="text-align: center">
                <th style="width: 2%">No</th>
                <th style="width: 7%">날짜</th>
                <th style="width: 4%">감사자</th>
                <th style="width: 4%">사업주</th>
                <th style="width: 35%">재고</th>
                <th style="width: 26%">환경</th>
                <th style="width: 22%">장비</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in modalAuditList" :key="idx">
                <td style="text-align: center; vertical-align: middle">
                  {{ idx + 1 }}
                </td>
                <td style="text-align: center; vertical-align: middle">
                  {{ r.AUDIT_DATE || r.DATE || r.date || "" }}
                </td>
                <td style="text-align: center; vertical-align: middle">
                  {{ r.AUDIT_NM || "" }}
                </td>
                <td style="text-align: center; vertical-align: middle">
                  {{ r.OWNER_NM || "" }}
                </td>
                <td style="white-space: pre-wrap">{{ r.STOCK_AUDIT || "" }}</td>
                <td style="white-space: pre-wrap">{{ r.ENV_AUDIT || "" }}</td>
                <td style="white-space: pre-wrap">{{ r.EQUI_AUDIT || "" }}</td>
              </tr>
              <tr v-if="modalAuditList.length === 0">
                <td colspan="7" style="text-align: center; color: #666">
                  조회된 감사내역이 없습니다.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="audit-modal-footer">
          <button class="btn-close" @click="closeAuditModal">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue3";
import { themeBalham } from "ag-grid-community";
import axios from "axios";

export default {
  name: "AmListPage",
  components: { AgGridVue },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      searchKeyword: "",
      gridApi: null,
      rowHeight: window.innerWidth > 1024 ? 25 : 22, // 행 높이
      columnDefs: [
        {
          headerName: "",
          valueGetter: (params) => {
            if (params.node && params.node.rowPinned === "top")
              return params.node ? params.node.rowIndex + 1 : "";
            return params.node ? params.node.rowIndex + 3 : "";
          },
          width: 30,
          pinned: "left",
          cellStyle: { textAlign: "center", backgroundColor: "#f0f0f0" },
        },
        {
          headerName: "",
          field: "SHOP_CD",
          pinned: "left",
          hide: true, // 숨김 컬럼
        },
        {
          headerName: "점번",
          field: "VAN_BIZ_NO",
          width: 35,
          flex: 1,
          pinned: "left",
          cellStyle: { textAlign: "center", backgroundColor: "#c8eeff" },
          colSpan: (params) => {
            if (params.node && params.node.rowPinned === "top") {
              return 3;
            }
            return 1;
          },
        },
        {
          headerName: "본부",
          field: "BRANCH_NM",
          width: 35,
          flex: 1,
          pinned: "left",
          cellStyle: { textAlign: "center", backgroundColor: "#c8eeff" },
        },
        {
          headerName: "매장명",
          field: "SHOP_NM",
          width: 70,
          flex: 1.5,
          pinned: "left",
          cellStyle: { textAlign: "center", backgroundColor: "#c8eeff" },
        },
        {
          headerName: "합계",
          children: [
            {
              headerName: "감사",
              field: "AD_0",
              width: 40,
              flex: 1,
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
              pinned: "left",
              colSpan: (params) => {
                if (
                  params.node &&
                  params.node.rowPinned === "top" &&
                  params.node.rowIndex === 0
                ) {
                  return 3;
                }
                return 1;
              },
            },
            {
              headerName: "폐업",
              field: "AD_1",
              width: 35,
              flex: 1,
              pinned: "left",
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
            },
            {
              headerName: "폐점",
              field: "AD_2",
              width: 35,
              flex: 1,
              pinned: "left",
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
            },
          ],
        },
        // 월별 컬럼: 1월~12월 
        ...this.generateMonthColumns(),
      ],
      rowData: [],
      pinnedTopRowData: [],
      gridOptions: {
        headerHeight: 20,
        groupHeaderHeight: 20, // 그룹 헤더의 높이를 25px로 설정합니다.
        rowHeight: 22,
        suppressHorizontalScroll: false,
        suppressDragLeaveHidesColumns: true, // 드래그 후 열 숨기기 방지
        tooltipShowMode: "whenTruncated",
        enableBrowserTooltips: false,
        tooltipShowDelay: 0,
        defaultColDef: {
          sortable: true,
          resizable: false,
          tooltipValueGetter: (params) => params.value,
          headerStyle: {
            color: "black",
            textAlign: "center",
            alignContent: "center",
          },
        },
      },
      theme: themeBalham.withParams({
        fontFamily: "Malgun Gothic",
        borderColor: "#808080",
        wrapperBorder: true,
        headerRowBorder: true,
        headerColumnBorderHeight: "100%",
        rowBorder: { style: "solid", width: 1 },
        columnBorder: { style: "solid", width: 1 },
      }),

      showAuditModal: false,
      modalShopCd: "",
      modalVanBizCd: "",
      modalbranchNm: "",
      modalShopNm: "",
      modalAuditList: [],
    };
  },
  computed: {
    yearOptions() {
      const startYear = 2023;
      const current = new Date().getFullYear();
      const options = [];
      for (let y = startYear; y <= current; y++) {
        options.push(y);
      }
      return options;
    },
  },
  methods: {
    generateMonthColumns() {
      const months = [];
      for (let m = 1; m <= 12; m++) {
        const monthLabel = `${m}월`;
        const mm = String(m).padStart(2, "0");
        months.push({
          headerName: monthLabel,
          children: [
            {
              headerName: "감사",
              field: `AD_${mm}_0`,
              minWidth: 35,
              flex: 1,
              cellStyle: { textAlign: "right" },
              colSpan: (params) => {
                if (
                  params.node &&
                  params.node.rowPinned === "top" &&
                  params.node.rowIndex === 0
                ) {
                  return 3;
                }
                return 1;
              },
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
            },
            {
              headerName: "폐업",
              field: `AD_${mm}_1`,
              minWidth: 35,
              flex: 1,
              cellStyle: { textAlign: "right" },
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
            },
            {
              headerName: "폐점",
              field: `AD_${mm}_2`,
              minWidth: 35,
              flex: 1,
              cellStyle: { textAlign: "right" },
              valueFormatter: (p) => {
                const v = Number(p.value ?? 0);
                return v === 0 ? "" : v.toLocaleString();
              },
            },
          ],
        });
      }
      return months;
    },

    async handleSearch() {
      await this.fetchData();
    },
    async fetchData() {
      try {
        const res = await axios.get("/api/amListPage/auditList", {
          params: { year: this.selectedYear },
        });

        // 데이터 행
        this.rowData = Array.isArray(res.data)
          ? res.data
          : res.data?.list || [];

        // 1) 보정: 각 행에 월별 AD_xx_y 값이 없으면 0 채우기 (안정성)
        for (const row of this.rowData) {
          // 각 행의 연간 합계(합계 그룹 값)
          row.AD_0 = 0;
          row.AD_1 = 0;
          row.AD_2 = 0;
          for (let m = 1; m <= 12; m++) {
            const mm = String(m).padStart(2, "0");
            row.AD_0 += Number(row[`AD_${mm}_0`] || 0);
            row.AD_1 += Number(row[`AD_${mm}_1`] || 0);
            row.AD_2 += Number(row[`AD_${mm}_2`] || 0);
          }
        }

        // 2) pinnedTopRowData: 총 계(첫줄) / 소 계(둘째줄)
        const totalsTop = {
          VAN_BIZ_NO: "총   계",
          BRANCH_NM: "",
          SHOP_NM: "",
        };
        const totalsSub = {
          VAN_BIZ_NO: "소   계",
          BRANCH_NM: "",
          SHOP_NM: "",
        };

        // 합계 그룹(연간 합계)과 월별 합계 계산
        let grand_AD0 = 0,
          grand_AD1 = 0,
          grand_AD2 = 0;

        for (let m = 1; m <= 12; m++) {
          const mm = String(m).padStart(2, "0");

          let msum0 = 0,
            msum1 = 0,
            msum2 = 0;
          for (const row of this.rowData) {
            msum0 += Number(row[`AD_${mm}_0`] || 0);
            msum1 += Number(row[`AD_${mm}_1`] || 0);
            msum2 += Number(row[`AD_${mm}_2`] || 0);
          }

          // 소계(둘째줄)에는 월별 항목별 합계를 표시
          totalsSub[`AD_${mm}_0`] = msum0;
          totalsSub[`AD_${mm}_1`] = msum1;
          totalsSub[`AD_${mm}_2`] = msum2;

          // 첫줄(총계)은 월별 '소계'(합계 4항목)를 표시하도록 AD_mm_0 칸에 넣음(헤더 보정 불가시)
          totalsTop[`AD_${mm}_0`] = msum0 + msum1 + msum2;
          totalsTop[`AD_${mm}_1`] = "";
          totalsTop[`AD_${mm}_2`] = "";

          // 누적 그랜드 합계
          grand_AD0 += msum0;
          grand_AD1 += msum1;
          grand_AD2 += msum2;
        }

        // 합계 그룹(맨 왼쪽)의 첫줄(총계): 전체 합(모든 월 + 항목)
        totalsTop.AD_0 = grand_AD0 + grand_AD1 + grand_AD2; // 총합(한셀)
        totalsTop.AD_1 = "";
        totalsTop.AD_2 = "";

        // 합계 그룹의 둘째줄(소계): 항목별 분해값
        totalsSub.AD_0 = grand_AD0;
        totalsSub.AD_1 = grand_AD1;
        totalsSub.AD_2 = grand_AD2;

        // pinned top rows: 총계(상단) / 소계(하단)
        this.pinnedTopRowData = [totalsTop, totalsSub];

        // 강제 갱신
        if (
          this.gridApi &&
          typeof this.gridApi.setPinnedTopRowData === "function"
        ) {
          this.gridApi.setPinnedTopRowData(this.pinnedTopRowData);
        }
      } catch (err) {
        console.error("fetchData 실패:", err);
        this.rowData = [];
        this.pinnedTopRowData = [];
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.fetchData();
    },

    handleResize() {
      const isWide = window.innerWidth > 1024;
      const newRowHeight = isWide ? 25 : 22;
      if (this.rowHeight !== newRowHeight) {
        this.rowHeight = newRowHeight;
        if (this.gridApi) this.gridApi.resetRowHeights();
      }
    },

    onQuickFilterChanged(event) {
      const filterValue = event.target.value; // 입력 필드 값 가져오기
      if (this.gridApi) {
        this.gridApi.setGridOption("quickFilterText", filterValue); // quickFilter 적용
      } else {
        console.error("Grid API is not initialized.");
      }
    },

    onCellDoubleClicked(params) {
      try {
        if (!params || !params.colDef) return;

        const field = params.colDef.field;

        // SHOP_NM 클릭: 감사 상세 팝업 오픈
        if (field === "SHOP_NM") {
          // 고정행이면 무시
          if (params.node && params.node.rowPinned === "top") return;
          const shopCd = params.data?.SHOP_CD || "";
          const shopNm = params.data?.SHOP_NM || "";
          const vanBizNo = params.data?.VAN_BIZ_NO || "";
          const branchNm = params.data?.BRANCH_NM || "";
          this.openAuditModalByShop(shopCd, shopNm, vanBizNo, branchNm);
          return;
        }

        // AD_0, AD_1, AD_2 컬럼만 처리
        if (field !== "AD_0" && field !== "AD_1" && field !== "AD_2") return;

        // 상단 고정행(총계/소계)을 더블클릭할 때 동작을 막으려면 아래 체크 유지
        if (params.node && params.node.rowPinned === "top") {
          return;
        }

        const shopCd = params.data?.SHOP_CD || "";
        if (!shopCd) {
          console.warn("매장코드가 없어 팝업을 열 수 없습니다.");
          return;
        }

        // URL 조합 (vShop_cd 대신 shopCd 사용)
        const vShop_cd = encodeURIComponent(String(shopCd));
        const index =
          field === "AD_0"
            ? 0
            : field === "AD_1"
            ? 1
            : field === "AD_2"
            ? 2
            : "";
        const url = `https://manager.tirebank.com/audit/report_post/0/${index}/${vShop_cd}`;

        // 새 탭으로 열기
        window.open(url, "_blank");
      } catch (err) {
        console.error("onCellDoubleClicked error:", err);
      }
    },

    async openAuditModalByShop(shopCd, shopNm, vanBizNo, branchNm) {
      try {
        if (!shopCd) {
          alert("매장 코드가 없습니다.");
          return;
        }
        this.modalShopCd = shopCd;
        this.modalShopNm = shopNm || "";
        this.modalVanBizCd = vanBizNo || "";
        this.modalbranchNm = branchNm || "";

        // 서버 호출: 백엔드 엔드포인트에 맞게 수정하세요
        // 예: GET /api/amListPage/auditStore?shopCd=001
        const res = await axios.get("/api/amListPage/modalAuditStore", {
          params: { shopCd: shopCd },
        });

        // res.data는 배열이어야 함 (SP 결과)
        this.modalAuditList = Array.isArray(res.data)
          ? res.data
          : res.data?.list || [];
        this.showAuditModal = true;
      } catch (err) {
        console.error("openAuditModalByShop error:", err);
        alert("감사내역 조회 중 오류가 발생했습니다.");
      }
    },

    closeAuditModal() {
      this.showAuditModal = false;
      this.modalShopCd = "";
      this.modalShopNm = "";
      this.modalVanBizCd = "";
      this.modalbranchNm = "";
      this.modalAuditList = [];
    },
  },
  mounted() {
    // selectedYear가 범위를 벗어나면 보정
    const current = new Date().getFullYear();
    if (this.selectedYear < 2023) this.selectedYear = 2023;
    if (this.selectedYear > current) this.selectedYear = current;

    window.addEventListener("resize", this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.am-list-page {
  display: flex;
  flex-direction: column;
  /* height: 100vh; */
  background: #f5f5f5;
  font-family: "Malgun Gothic", Arial, sans-serif;
}

.am-header {
  padding: 12px;
  background: white;
  border-bottom: 1px solid #ddd;
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  position: relative;
}

.year-month-selector {
  align-items: center;
  gap: 8px;
}

.year-select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
}

.title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-weight: 600;
  font-size: 1.25rem;
  white-space: nowrap;
  pointer-events: none;
}

.nav-btn {
  padding: 6px 12px;
  background: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
}

.nav-btn:hover {
  background: #e0e0e0;
}

.search-controls {
  display: flex;
  gap: 8px;
}

.search-input {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 150px;
}

.search-btn {
  padding: 6px 16px;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
}

.am-grid-container {
  flex: 1;
  overflow: hidden;
}

:deep(.ag-am-balham) {
  height: calc(100vh - 160px);
  --ag-cell-horizontal-padding: 3px; /* 셀 내부 좌우 패딩 */
}

:deep(.ag-am-balham .ag-cell) {
  font-size: 0.72rem;
  text-align: right;
  /* padding-right: 3px; */
}

:deep(.ag-am-balham .ag-header-cell-comp-wrapper) {
  display: flex;
  background-color: #f0f0f0;
  align-items: center;
  justify-content: center;
  color: black;
}

:deep(.ag-header-cell-label) {
  justify-content: center;
}

:deep(.ag-floating-top .ag-row, .ag-floating-top-container) {
  background-color: #ffd2da;
}

/* 1024px 초과일 때 폰트/행 높이 증가 */
@media (min-width: 1025px) {
  :deep(.ag-am-balham .ag-cell) {
    font-size: 0.87rem;
  }
}

/* 감사 상세 팝업 스타일 */
.audit-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.audit-modal {
  width: 95%;
  max-height: 85vh;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}
.audit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f2f2f2;
  border-bottom: 1px solid #ddd;
  font-weight: 700;
}
.audit-modal-body {
  padding: 8px;
  overflow: auto;
  flex: 1 1 auto;
}
.audit-info {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  font-weight: 600;
}
.audit-table {
  width: 110%;
  border-collapse: collapse;
  font-size: 0.72rem;
}

.audit-table th,
.audit-table td {
  border: 1px solid #ddd;
  padding: 2px;
  vertical-align: top;
}
.audit-modal-footer {
  padding: 8px 12px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #fafafa;
}
.btn-close {
  background: #eee;
  border: 1px solid #ccc;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
}
/* 반응형: 좁은 화면에서 폰트/패딩 조정 */
@media (min-width: 1025px) {
  .audit-table th,
  .audit-table td {
    font-size: 0.85rem;
  }
}
</style>
